import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addDays, format, startOfWeek } from "date-fns";
import es from "date-fns/locale/es";
import NavigationWeek from "../Guardias/NavigationWeek";
import {
  fetchFranjasHorarias,
  fetchOcupacionAulas,
  fetchReservasAula,
  fetchMisReservasFuturas,
  postReservarAula,
  deleteReservaAula,
} from "./Aulas-Api";

const ymd = (d) => format(d, "yyyy-MM-dd");

const DIAS_SEMANA = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];

export default function ReservaAulas({ setTitulo, userLogged }) {
  const myId = userLogged?.id;

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [franjas, setFranjas] = useState([]);
  const [baseOcupacion, setBaseOcupacion] = useState([]);
  const [reservasSemana, setReservasSemana] = useState([]);
  const [misReservas, setMisReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refetchKey, setRefetchKey] = useState(0);

  const [celdaAbierta, setCeldaAbierta] = useState(null);
  const [reservaConfirm, setReservaConfirm] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [filtroAulas, setFiltroAulas] = useState("LIBRES");

  const bump = useCallback(() => setRefetchKey((k) => k + 1), []);

  useEffect(() => {
    if (setTitulo) setTitulo("Reservar aulas");
  }, [setTitulo]);

  const hoy = new Date();
  const hoyYmd = ymd(hoy);
  const ahoraHHmm = format(hoy, "HH:mm");
  const limiteYmd = ymd(addDays(startOfWeek(hoy, { locale: es, weekStartsOn: 1 }), 11));

  const esFechaReservable = (fechaStr) => fechaStr >= hoyYmd && fechaStr <= limiteYmd;

  const franjaFinalizadaHoy = (franja) =>
    !!franja?.horaFin && franja.horaFin <= ahoraHHmm;

  const celdaReservable = (fechaStr, franja) => {
    if (!esFechaReservable(fechaStr)) return false;
    if (fechaStr === hoyYmd && franjaFinalizadaHoy(franja)) return false;
    return true;
  };

  const diasSemana = useMemo(() => {
    const inicio = startOfWeek(currentDate, { locale: es, weekStartsOn: 1 });
    return [0, 1, 2, 3, 4].map((i) => addDays(inicio, i));
  }, [currentDate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        fetchFranjasHorarias(setFranjas);
        const occ = await fetchOcupacionAulas({});
        if (alive) setBaseOcupacion(occ?.aulas || []);
      } catch (e) {
        console.error("Error cargando ocupación de aulas:", e);
        toast.error("No se pudo cargar la disponibilidad de aulas.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const fi = ymd(diasSemana[0]);
    const ff = ymd(diasSemana[4]);
    (async () => {
      try {
        const data = await fetchReservasAula({ fechaInicio: fi, fechaFin: ff });
        if (alive) setReservasSemana(data || []);
      } catch (e) {
        console.error("Error cargando reservas de la semana:", e);
        if (alive) toast.error("No se pudieron cargar las reservas de la semana.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [diasSemana, refetchKey]);

  useEffect(() => {
    let alive = true;
    if (!myId) return undefined;
    (async () => {
      try {
        const data = await fetchMisReservasFuturas(myId);
        if (alive) setMisReservas(data || []);
      } catch (e) {
        console.error("Error cargando mis reservas:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [myId, refetchKey]);

  const baseMap = useMemo(() => {
    const m = {};
    baseOcupacion.forEach((aula) => {
      (aula.ocupaciones || []).forEach((o) => {
        const k = `${aula.id}|${o.diaSemana.orden}|${o.franja.id}`;
        if (!(k in m)) {
          m[k] = {
            ...o,
            aulaId: aula.id,
            aulaCodigo: aula.codigo,
            aulaDescripcion: aula.descripcion,
            maxAlumnos: aula.maxAlumnos,
            tipoAula: aula.tipoAula,
          };
        }
      });
    });
    return m;
  }, [baseOcupacion]);

  const reservaMap = useMemo(() => {
    const m = {};
    reservasSemana.forEach((r) => {
      m[`${r.fecha}|${r.franjaId}|${r.aulaId}`] = r;
    });
    return m;
  }, [reservasSemana]);

  // Aulas titulares que quedan liberadas por la reserva de su profesor en una fecha/franja concreta. No se
  // cuenta la liberación provocada por una reserva propia (un profesor no se libera el aula a sí mismo).
  const liberacionesMap = useMemo(() => {
    const m = {};
    reservasSemana.forEach((r) => {
      const titularId = r.aulaTitularId;
      const esPropia = r.profesor?.id === myId;
      if (titularId != null && !esPropia) {
        m[`${r.fecha}|${r.franjaId}|${titularId}`] = r;
      }
    });
    return m;
  }, [reservasSemana, myId]);

  const estadoAula = (aula, diaOrden, franjaId, fechaStr) => {
    const occ = baseMap[`${aula.id}|${diaOrden}|${franjaId}`];
    const baseLibre = !!occ && occ.libre === true;
    const liberacion = liberacionesMap[`${fechaStr}|${franjaId}|${aula.id}`];
    // La reserva tiene prioridad sobre el horario (profesor desplazado que ya no dispone de su aula titular).
    const reserva = reservaMap[`${fechaStr}|${franjaId}|${aula.id}`];
    if (reserva) return { estado: "RESERVADA", reserva };
    if (baseLibre || liberacion) {
      return { estado: "LIBRE", occ, liberada: !!liberacion, liberacion };
    }
    return { estado: "HORARIO", occ };
  };

  // Aula titular del profesor logueado para la fecha/franja de la reserva en confirmación (si la tuviera).
  const miAulaTitular = useMemo(() => {
    if (!reservaConfirm || !myId) return null;
    // Los equipos no liberan el aula titular del profesor.
    if (reservaConfirm.aula?.esEquipo) return null;
    const diaOrden = new Date(`${reservaConfirm.fecha}T00:00:00`).getDay();
    return (
      baseOcupacion.find((aula) => {
        if (aula.id === reservaConfirm.aula.id) return false;
        const occ = baseMap[`${aula.id}|${diaOrden}|${reservaConfirm.franja.id}`];
        return occ?.profesorId === myId && occ?.libre !== true;
      }) || null
    );
  }, [reservaConfirm, baseOcupacion, baseMap, myId]);

  const resumenDia = (fechaStr, diaOrden, franja) => {
    let libres = 0;
    let reservadas = 0;
    baseOcupacion.forEach((aula) => {
      const r = estadoAula(aula, diaOrden, franja.id, fechaStr);
      if (r.estado === "LIBRE") libres += 1;
      else if (r.estado === "RESERVADA") reservadas += 1;
    });
    return { libres, reservadas };
  };

  const handleNavigate = (offset) => {
    setCurrentDate((prev) => addDays(prev, 7 * offset));
  };

  const abrirCelda = (fecha, franja) => {
    setFiltroAulas("LIBRES");
    setCeldaAbierta({ fecha: ymd(fecha), fechaObj: fecha, franja });
  };

  const aulasCelda = celdaAbierta
    ? baseOcupacion.filter((aula) => {
        const r = estadoAula(
          aula,
          celdaAbierta.fechaObj.getDay(),
          celdaAbierta.franja.id,
          celdaAbierta.fecha
        );
        if (filtroAulas === "LIBRES") return r.estado === "LIBRE";
        if (filtroAulas === "OCUPADAS") return r.estado !== "LIBRE";
        return true;
      })
    : [];

  const confirmarReserva = async () => {
    if (!reservaConfirm) return;
    const { fecha, franja, aula } = reservaConfirm;
    try {
      await postReservarAula({
        fecha,
        franjaId: franja.id,
        aulaId: aula.id,
        profesorId: myId,
        motivo,
      });
      toast.success(`Aula ${aula.codigo} reservada correctamente.`);
      setReservaConfirm(null);
      setMotivo("");
      bump();
    } catch (e) {
      const msg = e?.response?.data?.error || "No se pudo reservar el aula.";
      toast.error(msg);
    }
  };

  const cancelarReserva = async (reservaId) => {
    if (!window.confirm("¿Seguro que quieres cancelar esta reserva?")) return;
    try {
      await deleteReservaAula(reservaId, myId);
      toast.success("Reserva cancelada correctamente.");
      bump();
    } catch (e) {
      const msg = e?.response?.data?.error || "No se pudo cancelar la reserva.";
      toast.error(msg);
    }
  };

  if (!userLogged || !myId) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Debes iniciar sesión para reservar aulas.
      </Alert>
    );
  }

  return (
    <div className="container mt-4">
      <ToastContainer position="top-left" autoClose={5000} />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">Disponibilidad de aulas</h4>
        <div>
          <span className="badge bg-success me-2">Libre</span>
          <span className="badge bg-warning text-dark me-2">Reservada</span>
          <span className="badge bg-danger me-2">Ocupada (horario)</span>
        </div>
      </div>

      <NavigationWeek currentDate={currentDate} onNavigate={handleNavigate} />

      <Alert severity="info" sx={{ mb: 2 }}>
        Solo se puede reservar desde hoy (en franjas cuyo fin no haya pasado) hasta el viernes de la
        próxima semana ({format(new Date(`${limiteYmd}T00:00:00`), "dd/MM/yyyy")}).
      </Alert>

      {loading ? (
        <div className="text-center my-5">
          <CircularProgress />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 130 }}>Franja</th>
                  {diasSemana.map((dia, idx) => {
                    const esPasado = ymd(dia) < hoyYmd;
                    return (
                      <th key={idx} className="text-center" style={{ minWidth: 110 }}>
                        <div>{DIAS_SEMANA[idx]}</div>
                        <small className="text-muted">{format(dia, "dd/MM/yyyy")}</small>
                        {esPasado && <div className="text-muted small">(pasado)</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {franjas.map((franja) => (
                  <tr key={franja.id}>
                    <td>
                      <small>{franja.horaInicio}</small>
                      <small className="text-muted d-block">{franja.descripcion}</small>
                    </td>
                    {diasSemana.map((dia, idx) => {
                      const fechaStr = ymd(dia);
                      const disponible = celdaReservable(fechaStr, franja);
                      const { libres, reservadas } = resumenDia(fechaStr, idx + 1, franja);
                      return (
                        <td
                          key={idx}
                          className="text-center"
                          style={{ cursor: disponible ? "pointer" : "not-allowed" }}
                          onClick={() => (disponible ? abrirCelda(dia, franja) : undefined)}
                        >
                          {!disponible ? (
                            <span className="text-muted">—</span>
                          ) : (
                            <>
                              <div className="badge bg-success">{libres} libres</div>
                              {reservadas > 0 && (
                                <div className="badge bg-warning text-dark mt-1">{reservadas} reserv.</div>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <div className="mt-4">
        <h5>Mis reservas</h5>
        {misReservas.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            No tienes reservas de aulas pendientes.
          </Alert>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th>Aula</th>
                  <th>Fecha</th>
                  <th>Franja</th>
                  <th>Motivo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {misReservas.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.aula?.codigo}</strong>{" "}
                      <span className="text-muted">{r.aula?.descripcion}</span>
                    </td>
                    <td>{format(new Date(`${r.fecha}T00:00:00`), "dd/MM/yyyy")}</td>
                    <td>
                      {r.franja?.descripcion}{" "}
                      <small className="text-muted">
                        ({r.franja?.horaInicio} - {r.franja?.horaFin})
                      </small>
                    </td>
                    <td>{r.motivo || "-"}</td>
                    <td>
                      <Button size="small" color="error" variant="outlined" onClick={() => cancelarReserva(r.id)}>
                        Cancelar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Diálogo de detalle por celda (día + franja) */}
      <Dialog
        open={!!celdaAbierta}
        onClose={() => setCeldaAbierta(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {celdaAbierta
            ? `Aulas · ${DIAS_SEMANA[celdaAbierta.fechaObj.getDay() - 1]} ${format(
                celdaAbierta.fechaObj,
                "dd/MM/yyyy"
              )} · ${celdaAbierta.franja.descripcion} (${celdaAbierta.franja.horaInicio} - ${celdaAbierta.franja.horaFin})`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {!celdaAbierta ? null : baseOcupacion.length === 0 ? (
            <Alert severity="info">No hay aulas registradas.</Alert>
          ) : (
            <>
              <ToggleButtonGroup
                value={filtroAulas}
                exclusive
                size="small"
                onChange={(e, valor) => {
                  if (valor) setFiltroAulas(valor);
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="LIBRES">
                  <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} /> Libres
                </ToggleButton>
                <ToggleButton value="OCUPADAS">
                  <BlockIcon fontSize="small" sx={{ mr: 0.5 }} /> Ocupadas
                </ToggleButton>
                <ToggleButton value="TODAS">
                  <ListAltIcon fontSize="small" sx={{ mr: 0.5 }} /> Todas
                </ToggleButton>
              </ToggleButtonGroup>

              {aulasCelda.length === 0 ? (
                <Alert severity="info">No hay aulas que coincidan con el filtro seleccionado.</Alert>
              ) : (
                <div className="list-group">
                  {aulasCelda.map((aula) => {
                    const diaOrden = celdaAbierta.fechaObj.getDay();
                    const r = estadoAula(aula, diaOrden, celdaAbierta.franja.id, celdaAbierta.fecha);
                    const esMia = r.estado === "RESERVADA" && r.reserva?.profesor?.id === myId;
                    return (
                  <div key={aula.id} className="list-group-item d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{aula.codigo}</strong>
                      {aula.descripcion && <span className="text-muted ms-2">{aula.descripcion}</span>}
                      <div className="small">
                        {r.estado === "HORARIO" && (
                          <span>
                            Ocupada por horario
                            {r.occ?.grupos ? ` · Grupos: ${r.occ.grupos}` : ""}
                            {r.occ?.profesor ? ` · Profesor: ${r.occ.profesor}` : ""}
                            {r.occ?.detalleCelda ? ` · ${r.occ.detalleCelda}` : ""}
                          </span>
                        )}
                        {r.estado === "RESERVADA" && (
                          <span>
                            Reservada por {r.reserva?.profesor?.nombre}
                            {r.reserva?.grupos ? ` · Grupos: ${r.reserva.grupos}` : ""}
                            {r.reserva?.aulaTitularCodigo ? ` · libera ${r.reserva.aulaTitularCodigo}` : ""}
                            {r.reserva?.motivo ? ` · Motivo: ${r.reserva.motivo}` : ""}
                          </span>
                        )}
                        {r.estado === "LIBRE" && r.liberada && (
                          <span>
                            Liberada
                            {r.occ?.profesor ? ` por ${r.occ.profesor}` : ""}
                            {r.occ?.grupos ? ` · Grupos: ${r.occ.grupos}` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      {r.estado === "HORARIO" && <span className="badge bg-danger">Ocupada</span>}
                      {r.estado === "LIBRE" && r.liberada && (
                        <span className="badge bg-primary">Liberada</span>
                      )}
                      {r.estado === "RESERVADA" && !esMia && (
                        <span className="badge bg-warning text-dark">Reservada</span>
                      )}
                      {r.estado === "RESERVADA" && esMia && (
                        <Button size="small" color="error" variant="outlined" onClick={() => cancelarReserva(r.reserva.id)}>
                          Cancelar mi reserva
                        </Button>
                      )}
                      {r.estado === "LIBRE" && (
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          onClick={() => {
                            setReservaConfirm({ fecha: celdaAbierta.fecha, franja: celdaAbierta.franja, aula });
                            setMotivo("");
                          }}
                        >
                          Reservar
                        </Button>
                      )}
                    </div>
                  </div>
                );
                  })}
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCeldaAbierta(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de reserva */}
      <Dialog open={!!reservaConfirm} onClose={() => setReservaConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reservar aula</DialogTitle>
        <DialogContent>
          {reservaConfirm && (
            <>
              <Typography variant="body1" gutterBottom>
                ¿Confirmas la reserva del aula{" "}
                <strong>
                  {reservaConfirm.aula.codigo} {reservaConfirm.aula.descripcion || ""}
                </strong>
                ?<br />
                Día: <strong>{format(new Date(`${reservaConfirm.fecha}T00:00:00`), "dd/MM/yyyy")}</strong>
                <br />
                Franja: <strong>{reservaConfirm.franja.descripcion}</strong> (
                {reservaConfirm.franja.horaInicio} - {reservaConfirm.franja.horaFin})
              </Typography>
              {miAulaTitular && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Se liberará tu aula titular <strong>{miAulaTitular.codigo}</strong> en esta franja para que
                  otro profesor pueda reservarla.
                </Alert>
              )}
              <TextField
                autoFocus
                margin="dense"
                label="Motivo (opcional)"
                fullWidth
                variant="outlined"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReservaConfirm(null)}>Cancelar</Button>
          <Button onClick={confirmarReserva} variant="contained" color="success">
            Reservar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
